import zod from 'zod'
import {
  displaynameMaxLength,
  usernameMaxLength,
  limitsText,
  groupSlugMaxLength,
  usernameMinLength,
  minPwdLength,
  maxPwdLength,
  emailMinLength,
  userProfileLimits,
} from '../constants/limits'
import { api, NameScope } from '@wellesley/openapi'
import { getStore } from '@wellesley/store'
import { asyncDebounce } from 'helpers/asyncDebounce'
import trimAndCollapseSpaces from 'helpers/trimAndCollapseSpaces'

const emojiRegex = /\p{Extended_Pictographic}/u
const pronounsRegex = /^[a-zA-Z0-9\s/.]*$/

type UsernameValidatorProps = {
  showInlineError?: boolean
}

export const usernameValidator = ({
  showInlineError = true,
}: UsernameValidatorProps) =>
  zod
    .string()
    .trim()
    .min(1, showInlineError ? 'Username is required' : '')
    .min(
      usernameMinLength,
      showInlineError
        ? `Must be at least ${usernameMinLength} characters long`
        : '',
    )
    .max(usernameMaxLength, limitsText.long)
    .regex(/^\S*$/, 'Spaces are not allowed')
    .regex(
      /^[0-9a-zA-Z_]+[0-9a-zA-Z_.]*$/,
      'Only latin letters, digits, _ and . allowed',
    )

type IsUsernameTakenProps = {
  username: string
  groupId?: string
  nameScope?: NameScope
}

export const isUsernameTaken = asyncDebounce(
  async ({ username, groupId, nameScope = 'USER' }: IsUsernameTakenProps) => {
    try {
      const { taken, reserved } = await getStore()
        .dispatch(
          api.endpoints.getApiV1NamesCheck.initiate(
            {
              name: username,
              nameScope,
              scope: groupId,
            },
            { forceRefetch: true },
          ),
        )
        .unwrap()

      const exists = taken || reserved

      return {
        exists,
        message: reserved
          ? 'Reserved words detected. Please, try alternatives'
          : 'Username already in use. Please, try another one',
      }
    } catch {
      return {
        exists: false,
        message: '',
      }
    }
  },
  500,
)

export const isGlobalUrlTaken = isUsernameTaken

export const isChannelSlugTaken = asyncDebounce(
  async (groupId: string, slug: string) => {
    try {
      const { taken, reserved } = await getStore()
        .dispatch(
          api.endpoints.getApiV1NamesCheck.initiate(
            {
              name: slug,
              nameScope: 'USER',
              scope: groupId,
            },
            { forceRefetch: true },
          ),
        )
        .unwrap()

      return {
        exists: taken || reserved,
        message: taken
          ? 'Slug already in use. Please, try another one'
          : 'Reserved words detected. Please, try alternatives',
      }
    } catch {
      return {
        exists: false,
        message: '',
      }
    }
  },
  500,
)

export const displayNameValidator = zod
  .string()
  .trim()
  .min(1, 'Display name is required')
  .max(displaynameMaxLength)
  .regex(/^[^@]*$/, '@ is not allowed')
  .superRefine((value, ctx) => {
    const hasEmoji = emojiRegex.test(value)
    const hasAlphanumeric = /[a-zA-Z0-9]/.test(value)

    if (hasEmoji && !hasAlphanumeric) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Emojis alone are not allowed. Please add text',
      })
    }
  })

export const bioValidator = zod.string().max(1024, limitsText.long).optional()

const createSlugValidation = ({
  minLength,
  minErrorMessage,
  maxLength,
  regexErrorMessage,
}: {
  minLength: number
  minErrorMessage?: string
  maxLength: number
  regexErrorMessage: string
}) =>
  zod
    .string()
    .trim()
    .min(minLength, minErrorMessage)
    .max(maxLength, limitsText.long)
    .regex(/^\S*$/, 'Spaces are not allowed')
    .regex(/^[0-9a-zA-Z_]+[0-9a-zA-Z_.]*$/, regexErrorMessage)

export const groupSlugValidation = createSlugValidation({
  minLength: 2,
  maxLength: groupSlugMaxLength,
  regexErrorMessage: 'Invalid characters. ex.: group_name_1',
})

export const channelSlugValidation = createSlugValidation({
  minLength: 2,
  minErrorMessage: limitsText.short,
  maxLength: groupSlugMaxLength,
  regexErrorMessage: 'Only Latin letters, digits, _ and . allowed',
})

const usernamePattern = /^[0-9a-zA-Z_]+[0-9a-zA-Z_.]*$/
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/

export const usernameOrEmailValidator = zod
  .string()
  .trim()
  .min(1, '')
  .superRefine((value, ctx) => {
    const isEmailAttempt = value.includes('@')

    if (isEmailAttempt) {
      if (!emailPattern.test(value)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid email',
        })
      }
    } else {
      if (!usernamePattern.test(value)) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: 'Invalid characters',
        })
      }
    }
  })

export const pwdCaption = `Required: A-Z, a-z, 0-9, special character, min ${minPwdLength}, max ${maxPwdLength}`

export const passwordValidator = zod
  .string()
  .trim()
  .superRefine((value, ctx) => {
    if (emojiRegex.test(value)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Emojis are not allowed',
      })
    }

    const meetsAll =
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[^A-Za-z0-9]/.test(value) &&
      value.length >= minPwdLength &&
      value.length <= maxPwdLength

    if (!meetsAll) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: pwdCaption,
      })
    }
  })

export const emailValidator = zod
  .string()
  .trim()
  .min(emailMinLength, `Must be at least ${emailMinLength} characters long`)
  .regex(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
    'Email format is not valid',
  )
  .refine((value) => !emojiRegex.test(value), 'Emojis are not allowed')

export const isValidPronouns = (value: string): boolean => {
  return pronounsRegex.test(value)
}

export const pronounsValidator = zod
  .string()
  .max(
    userProfileLimits.pronouns,
    `Maximum ${userProfileLimits.pronouns} characters`,
  )
  .refine(
    (value) => isValidPronouns(value),
    'Special characters !@#$%^*()+={} []:;", are not allowed',
  )
  .transform(trimAndCollapseSpaces)
  .optional()

const locationSpecialCharsRegex = /[!@#$%^&*()]/
const numbersOnlyRegex = /^[0-9\s]+$/

export const locationValidator = zod
  .string()
  .max(userProfileLimits.location)
  .superRefine((value, ctx) => {
    if (!value) return

    if (locationSpecialCharsRegex.test(value)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: 'Special characters !@#$%^&*() are not allowed',
      })
      return
    }

    if (numbersOnlyRegex.test(value)) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: "Numbers alone aren't allowed. Please enter a valid location",
      })
    }
  })
  .optional()
